import { parseIsoInstant } from './timeGrid'
import { workerFullName } from './names'
import { groupStatusOf, primaryOf, selectionOf, stepOf, stepsOf } from './historyGroups'

// HistoryView (#207 §3, §8.1 and #205 §3, §8.1): from the rows of POST dataCorrection/search and the 200 of GET
// dataCorrection/{id} to what the «Correction history» tab draws. Texts come out as `{key, params}` (the English key of
// the catalogue), instants through `format(ms)`, durations as milliseconds for `formatDuration`.

const msOf = (instant) => (instant ? parseIsoInstant(instant).getTime() : Number.NaN)

/** A worker as the screen names it: «name surname», then the code, then «#id» when only the id is known (#207 §8.1). */
export const workerLabel = (worker) => (worker && (workerFullName(worker) || worker.code)) || (worker ? `#${worker.id}` : '')

/** A machine by its name, «#id» when it no longer exists. */
export const deviceLabel = (device) => (device && device.name) || (device ? `#${device.id}` : '')

/** A job by «code name», «#id» when it no longer exists. */
export const jobLabel = (job) => (job && [job.code, job.name].filter(Boolean).join(' ')) || (job ? `#${job.id}` : '')

/** A fabrication order by its code, «#id» when it no longer exists. */
export const fabricationOrderLabel = (order) => (order && order.code) || (order ? `#${order.id}` : '')

/** The names of a page or of a detail: `workers[]`, `jobs[]` and `fabricationOrders[]`, an id that is not there as «#id». */
export const catalogsOf = (response) => {
  const byId = (list) => Object.fromEntries((list || []).map((entry) => [entry.id, entry]))
  const workers = byId(response.workers)
  const jobs = byId(response.jobs)
  const orders = byId(response.fabricationOrders)
  return {
    worker: (id) => workerLabel(workers[id] || { id }),
    job: (id) => jobLabel(jobs[id] || { id }),
    fabricationOrder: (id) => fabricationOrderLabel(orders[id] || { id })
  }
}

export const STATUS_CHIP = {
  APPLIED: { label: 'Applied', color: 'success' },
  REVERTED: { label: 'Reverted', color: 'default' },
  PENDING: { label: 'Awaiting approval', color: 'warning' },
  APPROVED: { label: 'Approved, not applied', color: 'info' },
  // Story 211 (RN7): a close still applied whose trim was reverted.
  TRIM_REVERTED: { label: 'Trim reverted', color: 'warning' }
}

/** The chips of the steps of a two-step correction (RN7, maqueta M:1065): «1 · Close now», «2 · Trim» / «2 · Void». */
export const STEP_CHIP = {
  CLOSE: { number: 1, label: 'Close now', color: 'info' },
  TRIM: { number: 2, label: 'Trim', color: 'success' },
  VOID_STEP: { number: 2, label: 'Void', color: 'success' }
}

/** The chip of a status; an unknown one shows its code. */
export const statusChipOf = (status) => STATUS_CHIP[status] || { label: status, color: 'default' }

/**
 * «What» (#205 §8.1): the kind with the requested instant against what was recorded, e.g. «Real end 15:10 (recorded
 * 06:02)». A void names the stretch it removed. Story 211 (RN7, V5): the trim of a close says the record was open and closed
 * now; a close alone says when it was closed (never by its `intent.kind`, which is the one of its trim).
 */
export const whatOf = (header, format) => {
  const observed = header.observed || {}
  const instant = (text) => (text ? format(msOf(text)) : '—')
  const step = stepOf(header)
  if (step === 'CLOSE') return { key: 'Closed now {{time}} (it was open)', params: { time: instant(header.window.end) } }
  if (step === 'TRIM') return { key: 'Real end {{time}} (recorded open, closed now {{closed}})', params: { time: instant(header.intent.newInstant), closed: instant(observed.end) } }
  if (step === 'VOID_STEP') return { key: 'Voided {{start}} – {{end}} (it was open)', params: { start: instant(observed.start), end: instant(observed.end) } }
  switch (header.kind) {
    case 'ADJUST_END':
      return { key: 'Real end {{time}} (recorded {{recorded}})', params: { time: instant(header.intent.newInstant), recorded: instant(observed.end) } }
    case 'ADJUST_START':
      return { key: 'Real start {{time}} (recorded {{recorded}})', params: { time: instant(header.intent.newInstant), recorded: instant(observed.start) } }
    case 'VOID':
      return { key: 'Voided {{start}} – {{end}}', params: { start: instant(observed.start), end: instant(observed.end) } }
    default:
      return { key: 'Correction of type {{kind}}', params: { kind: header.kind } }
  }
}

/**
 * The time imputed to the corrected worker (#207 §8.1, the same rule as the preview, RN61): their rows of
 * `impact.imputations` on the corrected job, or all of theirs for a presence anchor. `impact: null` (applied before
 * V1.819, A3) ⇒ not available. A close (story 211, RN7) has `impact: null` because it does not apply: no imputed time at all.
 */
export const imputedOf = (header) => {
  if (stepOf(header) === 'CLOSE') return { notApplicable: true, available: false, beforeMs: 0, afterMs: 0, deltaMs: 0 }
  if (!header.impact) return { available: false, beforeMs: 0, afterMs: 0, deltaMs: 0 }
  const rows = header.impact.imputations
    .filter((row) => row.workerId === header.worker.id)
    .filter((row) => !header.job || row.jobReferenceId === header.job.id)
  return {
    available: true,
    beforeMs: rows.reduce((sum, row) => sum + row.beforeMillis, 0),
    afterMs: rows.reduce((sum, row) => sum + row.afterMillis, 0),
    deltaMs: rows.reduce((sum, row) => sum + row.deltaMillis, 0)
  }
}

const MINUTE_MS = 60000

/**
 * The change shown beside «before → after»: the difference of the two durations as they are shown (whole minutes, as
 * `formatDuration` cuts them), so «12 min → 10 min» reads «−2 min» and not the «−1 min» of the 1 min 55 s Sun measured.
 */
export const shownDeltaMs = (imputed) =>
  (Math.floor(imputed.afterMs / MINUTE_MS) - Math.floor(imputed.beforeMs / MINUTE_MS)) * MINUTE_MS

const percentOf = (amount) => (amount && amount.total ? `${Math.floor((amount.current * 100) / amount.total)} %` : '—')

/**
 * The job progress Sun recalculated (`derived[]`): the entry of the corrected job (else the first one); `[]` ⇒ nothing
 * moved. A progress that is not measured by time says so instead of percentages.
 */
export const progressOf = (header) => {
  const derived = header.derived || []
  if (derived.length === 0) return { changed: false }
  const entry = derived.find((item) => header.job && item.jobReferenceId === header.job.id) || derived[0]
  if (entry.progressCalculationType && entry.progressCalculationType !== 'TIME') return { changed: true, measured: false }
  return { changed: true, measured: true, before: percentOf(entry.before), after: percentOf(entry.after) }
}

/** A row of the list (#207 §8.1). */
export const historyRowOf = (row, format) => ({
  id: row.id,
  status: row.status,
  requestedAtMs: msOf(row.requestedAt),
  worker: workerLabel(row.worker),
  device: deviceLabel(row.device),
  what: whatOf(row, format),
  imputed: imputedOf(row),
  progress: progressOf(row)
})

const HOUR_MS = 3600000
const MARGIN_SHARE = 0.12

/** RN8 (M:951): the margin of the scale at each side of `observed`: max(1 h, 12 % of its duration). */
export const miniBarsMarginMs = (durationMs) => Math.max(HOUR_MS, durationMs * MARGIN_SHARE)

const barOf = (span, fromMs, toMs) => {
  const startMs = Math.max(fromMs, msOf(span.start))
  const endMs = Math.min(toMs, msOf(span.end))
  const length = toMs - fromMs
  return { left: ((startMs - fromMs) / length) * 100, width: (Math.max(0, endMs - startMs) / length) * 100 }
}

/**
 * RN8 [D-S8]: the two mini bars of a row, «Before» = `observed` and «After» = `requested` (empty for a VOID), on the same
 * scale (`observed` with its margin), with the corrected stretch `window` shaded in both; positions are % of the bar. The
 * contract only gives the intervals of the header, not the clock-ins the maqueta draws. `shown: false` for a close alone and
 * for a row with `observed: null` (before the epic).
 */
export const miniBarsOf = (header) => {
  if (stepOf(header) === 'CLOSE' || !header.observed) return { shown: false }
  const startMs = msOf(header.observed.start)
  const endMs = msOf(header.observed.end)
  const margin = miniBarsMarginMs(endMs - startMs)
  const fromMs = startMs - margin
  const toMs = endMs + margin
  return {
    shown: true,
    fromMs,
    toMs,
    before: [barOf(header.observed, fromMs, toMs)],
    after: header.requested ? [barOf(header.requested, fromMs, toMs)] : [],
    band: barOf(header.window, fromMs, toMs)
  }
}

/**
 * An element of the list (story 211, RN6-RN8): a single correction, a close and its trim grouped, or one step alone. It shows
 * the one of its trim (or of its only header), with the status of the group and the chips of its steps.
 */
export const historyItemOf = (item, format) => {
  const primary = primaryOf(item)
  return {
    ...historyRowOf(primary, format),
    key: item.key,
    status: groupStatusOf(item),
    steps: stepsOf(item),
    miniBars: miniBarsOf(primary),
    selection: selectionOf(item),
    item
  }
}

// ---- detail ----

export const TABLE_LABEL = {
  DEVICE_WORKER: 'Presence record',
  DEVICE_WORKER_FABRICATION_ORDER: 'Clock-in record',
  DEVICE_FABRICATION_ORDER: 'Job record',
  DEVICE_STATUS: 'Machine state record',
  FABRICATION_ORDER_PROGRESS: 'Job progress record',
  // Story 211 (RN17, #210 C4): the tables a «close now» touches too.
  JOB_REFERENCE: 'Job state record',
  WORKER_OTHER_TASK: 'Other task record'
}

/**
 * RN17: the label of a table by its enumerated name (`rows[].table`) or by its physical one (`details.table` of a blocker or
 * an error, e.g. `device_worker`); an unknown name is shown as it is.
 */
export const tableLabelOf = (name) => TABLE_LABEL[String(name || '').toUpperCase()] || name

export const HISTORY_STEP_LABEL = {
  PENDING: 'Correction requested',
  APPROVED: 'Correction approved',
  APPLIED: 'Correction applied',
  REVERTED: 'Correction reverted'
}

/** The rows shown before «Show all» (#205 §8.1, D12: a trace can have thousands of rows). */
export const DETAIL_ROWS_SHOWN = 50

// What a record says, readable: its stretch, its state and who/what it belongs to (#205 §8.1, I1). `null` facts (an
// INSERT before, a DELETE after) are «—».
const factsText = (facts, { format, names }) => {
  if (!facts) return ''
  const stretch = facts.start ? `${format(msOf(facts.start))} – ${facts.end ? format(msOf(facts.end)) : '…'}` : ''
  const owners = [
    facts.workerId ? names.worker(facts.workerId) : '',
    facts.fabricationOrderId ? names.fabricationOrder(facts.fabricationOrderId) : '',
    facts.jobReferenceId ? names.job(facts.jobReferenceId) : ''
  ].filter(Boolean)
  return [stretch, facts.stateCode || '', owners.join(' · ')].filter(Boolean).join(' · ')
}

/** The rows touched by the APPLY (the PRIMARY ones of the trace), split by operation (#205 §8.1). */
export const rowsTouchedOf = (rows) => {
  const primary = rows.filter((row) => row.phase === 'APPLY' && row.role === 'PRIMARY')
  const count = (operation) => primary.filter((row) => row.operation === operation).length
  return { total: primary.length, updated: count('UPDATE'), inserted: count('INSERT'), deleted: count('DELETE') }
}

/** The 200 of GET dataCorrection/{id} as the detail panel draws it. */
export const detailViewOf = (detail, format) => {
  const names = catalogsOf(detail)
  const windowStartMs = msOf(detail.window.start)
  const windowEndMs = msOf(detail.window.end)
  return {
    id: detail.id,
    status: detail.status,
    what: whatOf(detail, format),
    worker: workerLabel(detail.worker),
    device: deviceLabel(detail.device),
    job: detail.job ? jobLabel(detail.job) : '',
    fabricationOrder: detail.fabricationOrder ? fabricationOrderLabel(detail.fabricationOrder) : '',
    tapes: detail.intent.tapes || [],
    requestedBy: detail.requestedBy ? detail.requestedBy.userName : '',
    requestedAtMs: msOf(detail.requestedAt),
    reasonCode: detail.reasonCode,
    requestComment: detail.requestComment || '',
    reverted: detail.status === 'REVERTED'
      ? {
          by: detail.revertedBy ? detail.revertedBy.userName : '',
          atMs: msOf(detail.revertedAt),
          reasonCode: detail.revertReasonCode || '',
          comment: detail.revertComment || ''
        }
      : undefined,
    stretch: { startMs: windowStartMs, endMs: windowEndMs, durationMs: windowEndMs - windowStartMs },
    imputed: imputedOf(detail),
    progress: progressOf(detail),
    rowsTouched: rowsTouchedOf(detail.rows),
    rows: detail.rows.map((row) => ({
      key: `${row.phase}-${row.seq}`,
      phase: row.phase,
      table: tableLabelOf(row.table),
      operation: row.operation,
      derived: row.role === 'DERIVED',
      before: factsText(row.beforeFacts, { format, names }),
      after: factsText(row.afterFacts, { format, names })
    })),
    history: detail.statusHistory.map((entry, index) => ({
      key: `${index}-${entry.to}`,
      label: HISTORY_STEP_LABEL[entry.to] || entry.to,
      atMs: msOf(entry.occurredAt),
      actor: entry.actor ? entry.actor.userName : '',
      comment: entry.comment || ''
    }))
  }
}

/**
 * Story 211 (RN9, M:1064-1088): the detail of what was chosen, as ONE correction when it is a step of a «close now».
 * `close` and `step` are the details loaded of each step (either may be missing: not known, still loading or failed). With
 * both, the header, «what», KPIs and history of states are the ones of the trim; «Records touched» adds the PRIMARY rows of the
 * close («{n} of the close») and the table lists the rows of the close first, marked «close now».
 */
export const groupDetailViewOf = ({ chosen, close, step }, format) => {
  const main = step || chosen
  const base = detailViewOf(main, format)
  const device = main.device || {}
  const view = {
    ...base,
    device: deviceLabel(main.device),
    deviceExists: Boolean(device.name),
    target: { deviceId: device.id, workerId: main.worker ? main.worker.id : 0, window: main.window },
    steps: [],
    closeId: '',
    rowsOfClose: 0,
    trimAlreadyReverted: false
  }
  const kind = stepOf(main)
  if (kind === 'SINGLE') return view
  if (kind === 'CLOSE') {
    // A close alone: its trim is in another page or already reverted (an APPLIED close without REVERT_CHILD_FIRST, V4).
    // `revertibility` is null when the close is not APPLIED (#205 §3): a REVERTED close alone has no blocker.
    const childFirst = ((main.revertibility || {}).blockers || []).some((blocker) => blocker.message === 'DATA_CORRECTION_REVERT_CHILD_FIRST')
    return { ...view, steps: ['CLOSE'], trimAlreadyReverted: main.status === 'APPLIED' && !childFirst }
  }
  const steps = ['CLOSE', kind]
  if (!close) return { ...view, steps }
  const closeView = detailViewOf(close, format)
  const touched = base.rowsTouched
  const ofClose = closeView.rowsTouched
  return {
    ...view,
    steps,
    status: groupStatusOf({ close, step: main }),
    closeId: close.id,
    rowsOfClose: ofClose.total,
    rowsTouched: {
      total: touched.total + ofClose.total,
      updated: touched.updated + ofClose.updated,
      inserted: touched.inserted + ofClose.inserted,
      deleted: touched.deleted + ofClose.deleted
    },
    rows: [
      ...closeView.rows.map((row) => ({ ...row, key: `close-${row.key}`, closeNow: true })),
      ...base.rows
    ]
  }
}
